import axios from "axios";

/**
 * The envelope every ProHealth/Solutran endpoint replies with.
 *
 * `status` is the transport-level result, checked as `=== 0` for success at all
 * three call sites; the card processor's own verdict arrives separately as the
 * nested `data.statusCode === 1`. A non-zero `status` carries `message` and no
 * `data` — `pastPayments.spec.tsx:308` pins exactly that (`{ status: 1 }`, both
 * other fields absent).
 *
 * Deliberately NOT a discriminated union on `status`. That would let the existing
 * `=== 0` checks narrow `data` to present and silence the TS18048s at the six
 * `response.data.data.*` reads — but it requires claiming the failure arm is some
 * closed set of literals. Only `1` is ever observed, and only in the specs;
 * nothing establishes the live gateway cannot return another code. A
 * `{status: 0} | {status: number}` pair does not narrow at all (the second arm
 * still admits `0`), so the choice is between an unfounded closed union and
 * leaving the diagnostics standing. They stand — see the defect note below.
 *
 * SUSPECTED DEFECT: the success branches read `response.data.data.statusCode`,
 * `.balance`, `.statusReason`, `.orderId`, `.authorizeCode` and `.authorizeAmount`
 * without checking `data` is present —
 * `getBalance.tsx:22-26`, `makePayment.tsx:99-136`, `pastPayments.tsx:166-169`.
 * A `status: 0` reply that omitted `data` would throw a TypeError mid-handler,
 * after `makePayment` has already posted the payment authorization. No guard is
 * added here because inserting one would convert that crash into a silent no-op
 * on a money path.
 */
type ProHealthEnvelope<TPayload> = {
    status: number;
    message?: string;
    data?: TPayload;
};

/** The processor's verdict on a balance or payment authorization. */
type SolutranAuthorization = {
    statusCode?: number;
    statusReason?: string;
    balance?: string;
    /** The processor's id for the new payment, stored back onto the order. */
    orderId?: string;
    authorizeCode?: string;
    authorizeAmount?: string;
};

export const balanceInquiry = async (token: string, cardNumber: string) => {

    const requestURL = import.meta.env.VITE_PROHEALTH_API_ENDPOINT + "/balance-inquiry/" + cardNumber

    const requestHeaders = {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
    }

    return await axios.get<ProHealthEnvelope<SolutranAuthorization>>(requestURL, { headers: requestHeaders })
};

export const authorizePayment = async (token: string, cardNumber: string, org: string, productName: string) => {

    const requestURL = import.meta.env.VITE_PROHEALTH_API_ENDPOINT + "/authorize-payment/" + cardNumber

    const requestHeaders = {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
    }

    const data = {
        org: org,
        productName: productName
    }

    return await axios.post<ProHealthEnvelope<SolutranAuthorization>>(requestURL, data, { headers: requestHeaders })
};

export const voidTransaction = async (token: string, date: string, orderId: string, authCode: string) => {

    const requestURL = import.meta.env.VITE_PROHEALTH_API_ENDPOINT + "/void-transaction"

    const requestHeaders = {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
    }

    const data = {
        saleDate: date,
        orderId: orderId,
        authCode: authCode
    }

    return await axios.post<ProHealthEnvelope<SolutranAuthorization>>(requestURL, data, { headers: requestHeaders })
};